import { useCallback, useEffect, useState } from "react";
import {
  getCorrectionForDate,
  getCurrentBusinessContext,
  getReviewForDate,
  getReviewHistory,
  loadOrCreateProfile,
  type BusinessContextView,
  type ReviewHistoryItem,
} from "./application/round02-service";
import { loadGovernanceOverview, type GovernanceOverview } from "./application/round03-service";
import { db, initializeDatabase } from "./db/database";
import { dateKeyForInstant, shiftDateKey } from "./domain/business-date";
import type {
  BehaviorCorrection,
  CEOProfile,
  DailyCEOReview,
  FounderBottleneckEntry,
  PromiseEntry,
} from "./domain/types";
import { History } from "./features/History";
import { GovernanceHub } from "./features/GovernanceHub";
import { Onboarding } from "./features/Onboarding";
import { ReviewCompletion } from "./features/ReviewCompletion";
import { ReviewFlow } from "./features/ReviewFlow";
import { Settings } from "./features/Settings";
import { Today } from "./features/Today";
import { applyWaitingPWAUpdate, hasWaitingPWAUpdate, PWA_UPDATE_EVENT } from "./pwa";
import { AppFrame, type AppScreen } from "./ui/AppFrame";
import type { GovernanceArea } from "./ui/GovernanceTabs";

interface DashboardState {
  context?: BusinessContextView | undefined;
  todayReview?: DailyCEOReview | undefined;
  todayCorrection?: BehaviorCorrection | undefined;
  yesterdayCorrection?: BehaviorCorrection | undefined;
  promise?: PromiseEntry | undefined;
  bottleneck?: FounderBottleneckEntry | undefined;
  governance?: GovernanceOverview | undefined;
  history: ReviewHistoryItem[];
}

const EMPTY_DASHBOARD: DashboardState = { history: [] };

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Shanghai";
  } catch {
    return "Asia/Shanghai";
  }
}

export function App() {
  const [initialization, setInitialization] = useState<"loading" | "ready" | "error">("loading");
  const [profile, setProfile] = useState<CEOProfile>();
  const [dateKey, setDateKey] = useState("");
  const [dashboard, setDashboard] = useState<DashboardState>(EMPTY_DASHBOARD);
  const [screen, setScreen] = useState<AppScreen>("today");
  const [governanceArea, setGovernanceArea] = useState<GovernanceArea>("truth");
  const [online, setOnline] = useState(() => navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const refresh = useCallback(async (activeProfile: CEOProfile) => {
    const nextDateKey = dateKeyForInstant(new Date(), activeProfile.businessTimezone);
    const [
      context,
      todayReview,
      todayCorrection,
      yesterdayCorrection,
      history,
      promises,
      bottlenecks,
      governance,
    ] = await Promise.all([
      getCurrentBusinessContext(db),
      getReviewForDate(db, nextDateKey),
      getCorrectionForDate(db, nextDateKey),
      getCorrectionForDate(db, shiftDateKey(nextDateKey, -1)),
      getReviewHistory(db, 30),
      db.structuredRecords.where("recordType").equals("promise").toArray(),
      db.structuredRecords.where("recordType").equals("founder_bottleneck").toArray(),
      loadGovernanceOverview(db, nextDateKey),
    ]);
    const promise = promises
      .filter(
        (record): record is PromiseEntry =>
          record.recordType === "promise" &&
          record.deletionState === "active" &&
          (record.promiseStatus === "active" || record.promiseStatus === "overdue"),
      )
      .sort((left, right) => left.dueAt.localeCompare(right.dueAt))[0];
    const bottleneck = bottlenecks
      .filter(
        (record): record is FounderBottleneckEntry =>
          record.recordType === "founder_bottleneck" && record.deletionState === "active",
      )
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
    setDateKey(nextDateKey);
    setDashboard({
      context,
      todayReview,
      todayCorrection,
      yesterdayCorrection,
      history,
      promise,
      bottleneck,
      governance,
    });
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      await initializeDatabase(db);
      const nextProfile = await loadOrCreateProfile(db, browserTimezone());
      setProfile(nextProfile);
      await refresh(nextProfile);
      setInitialization("ready");
    } catch {
      setInitialization("error");
    }
  }, [refresh]);

  useEffect(() => {
    queueMicrotask(() => void bootstrap());
  }, [bootstrap]);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    const handleUpdate = () => setUpdateAvailable(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener(PWA_UPDATE_EVENT, handleUpdate);
    let active = true;
    const reconcileWaitingUpdate = () => {
      void hasWaitingPWAUpdate().then((waiting) => {
        if (active && waiting) setUpdateAvailable(true);
      });
    };
    reconcileWaitingUpdate();
    const updateTimer = window.setInterval(reconcileWaitingUpdate, 1_000);
    return () => {
      active = false;
      window.clearInterval(updateTimer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener(PWA_UPDATE_EVENT, handleUpdate);
    };
  }, []);

  if (initialization === "loading") {
    return (
      <main className="system-state">
        <span className="brand">CEO OS</span>
        <h1>正在打开本地驾驶舱</h1>
        <p>检查数据库版本和未完成草稿…</p>
      </main>
    );
  }

  if (initialization === "error" || !profile) {
    return (
      <main className="system-state">
        <span className="brand">CEO OS</span>
        <h1>本地数据库未能初始化</h1>
        <p>现有数据没有被空状态覆盖。请确认浏览器允许 IndexedDB，释放存储空间后重试。</p>
        <button
          type="button"
          className="primary-button"
          onClick={() => {
            setInitialization("loading");
            void bootstrap();
          }}
        >
          重新检查
        </button>
      </main>
    );
  }

  if (profile.onboardingStatus !== "completed") {
    return (
      <Onboarding
        database={db}
        initialProfile={profile}
        dateKey={dateKey}
        onComplete={(completed) => {
          setProfile(completed);
          void refresh(completed);
        }}
      />
    );
  }

  let content;
  if (screen === "today") {
    content = (
      <Today
        database={db}
        profile={profile}
        context={dashboard.context}
        todayReview={dashboard.todayReview}
        todayCorrection={dashboard.todayCorrection}
        yesterdayCorrection={dashboard.yesterdayCorrection}
        promise={dashboard.promise}
        bottleneck={dashboard.bottleneck}
        online={online}
        onStartReview={() => setScreen("review")}
        governance={dashboard.governance}
        onOpenGovernance={(area) => {
          setGovernanceArea(area);
          setScreen("governance");
        }}
        onRefresh={() => refresh(profile)}
      />
    );
  } else if (screen === "review") {
    content =
      dashboard.todayReview &&
      (dashboard.todayReview.reviewStatus === "completed" ||
        dashboard.todayReview.reviewStatus === "exported") &&
      dashboard.todayCorrection ? (
        <ReviewCompletion
          database={db}
          review={dashboard.todayReview}
          correction={dashboard.todayCorrection}
          profile={profile}
          onBack={() => setScreen("today")}
          onExported={() => refresh(profile)}
          onOpenGovernance={(area) => {
            setGovernanceArea(area);
            setScreen("governance");
          }}
        />
      ) : (
        <ReviewFlow
          database={db}
          profile={profile}
          dateKey={dateKey}
          onBack={() => setScreen("today")}
          onComplete={(result) => {
            setDashboard((current) => ({
              ...current,
              todayReview: result.review,
              todayCorrection: result.correction,
            }));
          }}
        />
      );
  } else if (screen === "governance" && dashboard.governance) {
    content = (
      <GovernanceHub
        key={governanceArea}
        database={db}
        overview={dashboard.governance}
        profile={profile}
        context={dashboard.context}
        dateKey={dateKey}
        online={online}
        initialArea={governanceArea}
        onAreaChange={setGovernanceArea}
        onRefresh={() => refresh(profile)}
      />
    );
  } else if (screen === "history") {
    content = <History items={dashboard.history} />;
  } else {
    content = (
      <Settings
        database={db}
        profile={profile}
        onProfileChange={(nextProfile) => {
          setProfile(nextProfile);
          void refresh(nextProfile);
        }}
        onContextChange={() => refresh(profile)}
        onDataReset={async () => {
          const nextProfile = await loadOrCreateProfile(db, browserTimezone());
          setProfile(nextProfile);
          await refresh(nextProfile);
          setScreen("today");
        }}
      />
    );
  }

  return (
    <AppFrame
      screen={screen}
      onNavigate={setScreen}
      online={online}
      updateAvailable={updateAvailable}
      onApplyUpdate={() => {
        if (applyWaitingPWAUpdate()) setUpdateAvailable(false);
      }}
    >
      {content}
    </AppFrame>
  );
}
